import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAtom } from 'jotai'
import sortBy from 'lodash/sortBy'

import { SimpleInput, Tabs } from '@makyoapp/makyo-ui'

import { editSettingsAtom } from '@/lib/jotai'
import { fromSdkPageBody } from '@/adapters/__project__/BuilderPageAdapter'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { DEFAULT_COMPONENTS_GROUPS } from '@/shared/builder'
import { flatten, getAncestors } from '@/shared/builder-navigator'
import { ComponentPanelTab } from '@/shared/builder-ui'

import ComponentActionPanel from './ComponentActionPanel'
import ComponentBehaviorPanel from './ComponentBehaviourPanel'
import ComponentSettingField from './ComponentSettingField'
import ComponentSettingsAccordion from './ComponentSettingsAccordion'

interface SearchFieldProps {
  setSearch: (data: string) => void
  search: string
}

function SearchField({ search, setSearch }: SearchFieldProps) {
  const { t } = useTranslation()
  return (
    <div className='component-filter border-b border-b-gray-300 dark:border-b-gray-600'>
      <div className='component-search w-full'>
        <SimpleInput
          id='data-search-input'
          onChange={(e) => {
            setSearch(e.currentTarget.value)
          }}
          inputClassName='w-full! txt'
          placeholder={t('Search')}
          outlined
          value={search}
          showClear={Boolean(search)}
          onClear={() => setSearch('')}
        />
      </div>
    </div>
  )
}

function ComponentSetting() {
  const [device] = useActiveDevice()
  const { appClient, components, activeComponentTab, setActiveComponentTab } = useAppBuilderContext()

  const { activeComponent, activeLayerElements, activeLayerClient } = useActiveLayerContext()
  const activeComponentId = activeComponent?.id ?? ''

  const [searchData, setSearchData] = useState('')
  const [searchDesign, setSearchDesign] = useState('')
  const [searchAction, setSearchAction] = useState('')
  const [searchBehavior, setSearchBehavior] = useState('')

  // Get `apply all settings` state
  const [_, setActiveEditSetting] = useAtom(editSettingsAtom)

  // Reset apply all cache when the component is changed
  useEffect(() => {
    setActiveEditSetting({ device, settingKey: '', updatedValues: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComponentId, device])

  // Find the active component from the components list
  const theComp = useMemo(() => {
    return components
      .find((g) => g.id === activeComponent?.groupId)
      ?.components.find((c) => c.compId === activeComponent?.compId)
  }, [components, activeComponent])

  // Identify if the active component has data, design, behavior, or action settings
  const hasData = theComp?.settings.dataGroup || theComp?.settings.data
  const hasDesign = theComp?.settings.design || theComp?.settings.designGroup
  const hasBehavior = theComp?.settings.behavior && Object.values(theComp?.settings.behavior).some((p) => p)
  const hasAction = theComp?.settings.action

  // Determine the active tab in the component settings panel based on the available settings
  let initialActiveKey: ComponentPanelTab
  if (hasData) {
    initialActiveKey = ComponentPanelTab.Data
  } else if (hasDesign) {
    initialActiveKey = ComponentPanelTab.Design
  } else {
    initialActiveKey = ComponentPanelTab.Behavior
  }

  const [activeKey, setActiveKey] = useState<ComponentPanelTab>(initialActiveKey)

  useEffect(() => {
    setActiveKey(initialActiveKey)
  }, [activeComponentId, initialActiveKey])

  // Update the active tab when the available settings change
  useEffect(() => {
    if (activeKey === ComponentPanelTab.Data && !hasData) {
      setActiveKey(hasDesign ? ComponentPanelTab.Design : ComponentPanelTab.Behavior)
    }
    if (activeKey === ComponentPanelTab.Design && !hasDesign) {
      setActiveKey(hasData ? ComponentPanelTab.Data : ComponentPanelTab.Behavior)
    }
  }, [hasDesign, hasData, activeKey, setActiveKey])

  // Flatten the settings of the active component
  const flattenedSettings = useMemo(() => {
    const flattened = [...(theComp?.settings.data ?? []), ...(theComp?.settings.design ?? [])]
    // If the active component has data group settings, add them to the flattened settings list
    if (theComp?.settings.dataGroup) {
      const stgs = Object.values(theComp?.settings.dataGroup)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          flattened.push(...s.items)
        }
      }
    }

    // If the active component has design group settings, add them to the flattened settings list
    if (theComp?.settings.designGroup) {
      const stgs = Object.values(theComp?.settings.designGroup)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          flattened.push(...s.items)
        }
      }
    }

    // If the active component has custom behavior settings, add them to the flattened settings list
    if (theComp?.settings.behavior?.custom) {
      const stgs = Object.values(theComp?.settings.behavior?.custom)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          flattened.push(...s.items)
        }
      }
    }
    return flattened
  }, [theComp?.settings])

  // Include global Header/Footer in elements for accurate ancestry (mirrors Navigator logic)
  const elementsWithGlobals = useMemo(() => {
    const base = activeLayerElements || []
    if (!activeLayerClient || activeLayerClient.type !== 'page') return base

    const globals = (appClient?.builderSettings as any)?._Globals
    const globalsBody: any[] = (globals?._Body || []) as any[]
    if (!globalsBody.length) return base

    const pageHasHeader = base.some((e) => e.compId === 'header')
    const pageHasFooter = base.some((e) => e.compId === 'footer')

    const showHeader = !pageHasHeader
    const showFooter = !pageHasFooter

    const headerBodies = globalsBody.filter((b) => b._ComponentID === 'header' && b._GroupID === 'globals')
    const footerBodies = globalsBody.filter((b) => b._ComponentID === 'footer' && b._GroupID === 'globals')

    const headerEls = showHeader ? fromSdkPageBody(headerBodies as any, DEFAULT_COMPONENTS_GROUPS) : []
    const footerEls = showFooter ? fromSdkPageBody(footerBodies as any, DEFAULT_COMPONENTS_GROUPS) : []

    return [...headerEls, ...base, ...footerEls]
  }, [activeLayerElements, activeLayerClient, appClient?.builderSettings])

  // Flatten the components of the active page (plus globals) for easy traversal and manipulation
  const flattenedComponents = useMemo(() => {
    return flatten(elementsWithGlobals || [])
  }, [elementsWithGlobals])

  // Obtain the ancestors of the active component, useful to determine the context or hierarchy
  // where the component is located in the design
  const ancestors = useMemo(() => {
    return activeComponent ? getAncestors(flattenedComponents, activeComponent.id) : []
  }, [flattenedComponents, activeComponent])

  // Conditionally display a notice if a form component is not located inside a form container
  const formNotice = useMemo(() => {
    if (theComp?.isForm && !ancestors.find((a) => a.compId === 'form')) {
      return (
        <Trans>
          Attention: The form components must be located inside a form container. Kindly use the navigator on your right
          to drag this component into the form container.
        </Trans>
      )
    }
    return null
  }, [theComp?.isForm, ancestors])

  const sortDataAscending = (data: any) => {
    if (!data) return {}

    try {
      const sortedKeys = sortBy(Object.keys(data))
      const sortedData = {} as any

      sortedKeys.forEach((key) => {
        sortedData[key] = data[key]
      })
      // NOTE: order by translated data
      return sortedData
    } catch (error) {
      return data
    }
  }

  const filterGroup = <T extends Record<string, any>>(
    group: T,
    searchQuery: string | undefined,
    outputType: 'array' | 'object' = 'object'
  ) => {
    if (!searchQuery) {
      return outputType === 'array' ? Object.values(group) : group
    }

    if (outputType === 'array') {
      return Object.values(group).filter(
        (item) =>
          item.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (!group) return {} as T

    const filteredGroup = {} as T
    Object.keys(group).forEach((key) => {
      if (key.toLowerCase().includes(searchQuery.toLowerCase())) {
        ;(filteredGroup as any)[key] = group[key]
      }
    })
    return filteredGroup
  }

  const sortedDataGroup: any = theComp?.settings?.dataGroup // sortDataAscending(theComp?.settings?.dataGroup)
  const dataGroup = useMemo(() => filterGroup(sortedDataGroup, searchData), [searchData, sortedDataGroup])

  const filteredData = useMemo(() => {
    return theComp?.settings?.data?.filter(
      (s) =>
        s.id.toLowerCase().includes(searchData.toLowerCase()) ||
        s.label.toLowerCase().includes(searchData.toLowerCase())
    )
  }, [searchData, theComp?.settings?.data])

  const sortedDesignGroup = sortDataAscending(theComp?.settings?.designGroup)
  const designGroup = useMemo(() => filterGroup(sortedDesignGroup, searchDesign), [searchDesign, sortedDesignGroup])

  const filteredDesign = useMemo(() => {
    return theComp?.settings?.design?.filter(
      (s) =>
        s.id.toLowerCase().includes(searchDesign.toLowerCase()) ||
        s.label.toLowerCase().includes(searchDesign.toLowerCase())
    )
  }, [searchDesign, theComp?.settings?.design])

  const sortedActionGroup = sortDataAscending(theComp?.settings?.action)
  const actionGroup = useMemo(
    () => filterGroup(sortedActionGroup, searchAction, 'array'),
    [searchAction, sortedActionGroup]
  )

  const sortedBehaviorGroup = sortDataAscending(theComp?.settings?.behavior)
  const behaviorGroup = useMemo(
    () => filterGroup(sortedBehaviorGroup, searchBehavior),
    [searchBehavior, sortedBehaviorGroup]
  )

  useEffect(() => {
    if (activeComponentTab) {
      setActiveKey(activeComponentTab)
    }
  }, [activeComponentTab])

  return formNotice ? (
    <div className='red p-4 m-4 border border-red-600 dark:border-red-400 rounded-sm font-bold leading-6'>
      {formNotice}
    </div>
  ) : (
    <Tabs
      activeKey={activeKey}
      defaultActiveKey={
        hasData ? ComponentPanelTab.Data : hasDesign ? ComponentPanelTab.Design : ComponentPanelTab.Behavior
      }
    >
      <Tabs.Nav
        onSelect={(eventKey) => {
          setActiveKey((eventKey as ComponentPanelTab) ?? ComponentPanelTab.Data)
        }}
      >
        {hasData && (
          <Tabs.NavItem eventKey={ComponentPanelTab.Data} className='mk-builder-component-nav-item'>
            <Trans>Data</Trans>
          </Tabs.NavItem>
        )}
        {hasDesign && (
          <Tabs.NavItem eventKey={ComponentPanelTab.Design} className='mk-builder-component-nav-item'>
            <Trans>Design</Trans>
          </Tabs.NavItem>
        )}
        {hasAction && (
          <Tabs.NavItem eventKey={ComponentPanelTab.Action} className='mk-builder-component-nav-item'>
            <Trans>Action</Trans>
          </Tabs.NavItem>
        )}
        {hasBehavior && (
          <Tabs.NavItem eventKey={ComponentPanelTab.Behavior} className='mk-builder-component-nav-item'>
            <Trans>Behavior</Trans>
          </Tabs.NavItem>
        )}
      </Tabs.Nav>

      {hasData && (
        <Tabs.Panel eventKey='data' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchData} setSearch={setSearchData} />
          {dataGroup && (
            <ComponentSettingsAccordion
              activeComponentId={activeComponentId}
              comp={theComp}
              settings={dataGroup}
              flattenedSettings={flattenedSettings}
              flattenedComponents={flattenedComponents}
              name='data'
              ancestors={ancestors}
            />
          )}
          <div className='p-4'>
            {filteredData?.map((s, si) => (
              <ComponentSettingField
                key={`${activeComponentId}-data-${s.id}${si}`}
                comp={theComp}
                {...s}
                ancestors={ancestors}
                flattenedSettings={flattenedSettings}
                flattenedComponents={flattenedComponents}
              />
            ))}
          </div>
        </Tabs.Panel>
      )}
      {hasDesign && (
        <Tabs.Panel eventKey='design' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchDesign} setSearch={setSearchDesign} />
          {designGroup && (
            <ComponentSettingsAccordion
              activeComponentId={activeComponentId}
              comp={theComp}
              settings={designGroup}
              flattenedSettings={flattenedSettings}
              flattenedComponents={flattenedComponents}
              name='design'
              ancestors={ancestors}
            />
          )}
          <div className='p-4'>
            {filteredDesign?.map((s, si) => (
              <ComponentSettingField
                key={`${activeComponentId}-design-${s.id}${si}`}
                comp={theComp}
                {...s}
                ancestors={ancestors}
                flattenedSettings={flattenedSettings}
                flattenedComponents={flattenedComponents}
              />
            ))}
          </div>
        </Tabs.Panel>
      )}
      {theComp && hasAction && (
        <Tabs.Panel eventKey='action' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchAction} setSearch={setSearchAction} />
          <ComponentActionPanel comp={theComp} ancestors={ancestors} actions={actionGroup ?? []} />
        </Tabs.Panel>
      )}
      {theComp && hasBehavior && (
        <Tabs.Panel eventKey='behavior' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchBehavior} setSearch={setSearchBehavior} />
          <ComponentBehaviorPanel
            comp={theComp}
            settings={behaviorGroup ?? {}}
            flattenedSettings={flattenedSettings}
            flattenedComponents={flattenedComponents}
            ancestors={ancestors}
          />
        </Tabs.Panel>
      )}
    </Tabs>
  )
}

export default ComponentSetting
